import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';

import { measurePanelAnchor, PanelAnchor } from '../../shared/panel-position.util';

interface DayCell {
  label: string;
  date: Date | null;
  selected: boolean;
  disabled: boolean;
  empty: boolean;
}

@Component({
  selector: 'app-date-picker-field',
  templateUrl: './date-picker-field.component.html',
  styleUrls: ['./date-picker-field.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DatePickerFieldComponent implements OnChanges, OnDestroy {
  @Input() label: string;
  @Input() placeholder = 'Select a date…';
  @Input() required = false;
  @Input() value = '';
  @Input() min = '';
  @Input() max = '';
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('trigger', { static: false }) triggerRef: ElementRef<HTMLButtonElement>;

  open = false;
  viewYear: number;
  viewMonth: number;
  anchor: PanelAnchor | null = null;

  readonly MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  readonly WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  days: DayCell[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.value || changes.min) && this.open) {
      this._buildDays();
    }
  }

  ngOnDestroy(): void {
    this.open = false;
  }

  get displayText(): string {
    if (!this.value) {
 return null;
}
    const d = this._parse(this.value);
    return d ? `${this.MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : null;
  }

  get monthLabel(): string {
    return `${this.MONTHS[this.viewMonth]} ${this.viewYear}`;
  }

  get panelStyle(): { [k: string]: string } {
    const a = this.anchor;
    return {
      position: 'fixed',
      left: a ? `${a.left}px` : '0px',
      top: a ? `${a.top}px` : '100%',
      width: a ? `${Math.max(300, a.width)}px` : '300px',
      'max-height': a ? `${a.maxH}px` : '348px',
      'z-index': '1051',
      'overflow-y': 'auto'
    };
  }

  toggle(): void {
    if (this.open) {
      this.open = false;
      return;
    }
    this._initView();
    this._buildDays();
    this.anchor = this._measure();
    this.open = true;
  }

  close(): void {
    this.open = false;
  }

  prevMonth(): void {
    if (this.viewMonth === 0) {
      this.viewMonth = 11;
      this.viewYear--;
    } else {
      this.viewMonth--;
    }
    this._buildDays();
  }

  nextMonth(): void {
    if (this.viewMonth === 11) {
      this.viewMonth = 0;
      this.viewYear++;
    } else {
      this.viewMonth++;
    }
    this._buildDays();
  }

  pickDay(cell: DayCell): void {
    if (cell.empty || cell.disabled) {
 return;
}
    this.valueChange.emit(this._fmt(cell.date));
    this.close();
  }

  private _measure(): PanelAnchor | null {
    const el = this.triggerRef && this.triggerRef.nativeElement;
    return measurePanelAnchor(el, 348);
  }

  private _initView(): void {
    const sel = this._parse(this.value);
    const base = sel || this._parse(this.min) || new Date();
    this.viewYear = base.getFullYear();
    this.viewMonth = base.getMonth();
  }

  private _buildDays(): void {
    const minDate = this.min ? this._startOfDay(this._parse(this.min)) : null;
    const maxDate = this.max ? this._startOfDay(this._parse(this.max)) : null;
    const sel = this._parse(this.value);
    const first = new Date(this.viewYear, this.viewMonth, 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    const cells: DayCell[] = [];

    for (let i = 0; i < startPad; i++) {
      cells.push({ label: '', date: null, selected: false, disabled: true, empty: true });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(this.viewYear, this.viewMonth, d);
      const tooEarly = minDate && date < minDate;
      const tooLate = maxDate && date > maxDate;
      const selected = sel &&
        sel.getFullYear() === this.viewYear &&
        sel.getMonth() === this.viewMonth &&
        sel.getDate() === d;
      cells.push({ label: String(d), date, selected: !!selected, disabled: !!(tooEarly || tooLate), empty: false });
    }
    this.days = cells;
  }

  private _parse(v: string): Date | null {
    if (!v) {
 return null;
}
    const p = v.split('-');
    if (p.length !== 3) {
 return null;
}
    return new Date(+p[0], +p[1] - 1, +p[2]);
  }

  private _fmt(d: Date): string {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }

  private _startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
}
